import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    IconButton,
    Chip,
    InputAdornment,
    CircularProgress,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    collection,
    getDocs,
    query as fsQuery,
    orderBy,
    where,
    limit as fsLimit,
    startAfter,
    DocumentSnapshot,
    getCountFromServer,
    Timestamp
} from 'firebase/firestore';
import { firestoreInstance } from 'config/ApiService';

interface AutoDischargeItem {
    id: string;
    discharge_date?: Timestamp;
    discharge_reason?: string;
    customer_name?: string;
    type?: string; // e.g., reason/type of auto discharge
    user_id: string;
    treatment_notes?: string;
    status: 'PENDING' | 'SENT' | 'FAILED' | 'COMPLETED';
}

export const PhioengageAutodischargeQueue: React.FC = () => {
    const [rows, setRows] = useState<AutoDischargeItem[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
    const [pageCursors, setPageCursors] = useState<Array<DocumentSnapshot | null>>([]);

    // Tokenize free-text for array-contains-any against `search_index`
    const searchTokens = useMemo(() => {
        const q = (searchQuery || '').toLowerCase().trim();
        if (!q) return [] as string[];
        const tokens = q
            .split(/[^a-z0-9]+/i)
            .map((t) => t.trim())
            .filter((t) => t.length > 0);
        return Array.from(new Set(tokens)).slice(0, 10);
    }, [searchQuery]);

    const buildBaseQuery = () => {
        const colRef = collection(firestoreInstance(), 'phioengage_auto_discharge_queue');
        const constraints: any[] = [];
        constraints.push(orderBy('created_at', 'desc'));
        if (searchQuery) {
            constraints.push(where('search_index', 'array-contains-any', [searchQuery]));
        }
        return fsQuery(colRef, ...constraints);
    };

    const fetchTotal = async () => {
        try {
            const q = buildBaseQuery();
            const agg = await getCountFromServer(q);
            setTotalCount(agg.data().count);
        } catch (e: any) {
            console.error('Failed to fetch total count', e);
            setError('Failed to load total count');
        }
    };

    const fetchPage = async (pageIndex: number) => {
        setLoading(true);
        setError(null);
        try {
            const base = buildBaseQuery();
            const startCursor = pageIndex > 0 ? (pageCursors[pageIndex - 1] ?? null) : null;
            const q = startCursor
                ? fsQuery(base, startAfter(startCursor), fsLimit(rowsPerPage))
                : fsQuery(base, fsLimit(rowsPerPage));
            const snap = await getDocs(q);
            const items: AutoDischargeItem[] = snap.docs.map((d) => {
                const data = d.data() as any;
                return {
                    id: d.id,
                    discharge_date: data.discharge_date,
                    discharge_reason: data.discharge_reason,
                    customer_name: data.customer_name,
                    type: data.type,
                    user_id: data.user_id,
                    treatment_notes: data.treatment_notes,
                    status: data.status
                } as AutoDischargeItem;
            });
            const lastVisible = snap.docs[snap.docs.length - 1] ?? null;
            setRows(items);
            setPageCursors((prev) => {
                const next = prev.slice();
                next[pageIndex] = lastVisible;
                return next;
            });
        } catch (e: any) {
            console.error('Failed to fetch page', e);
            setError('Failed to load auto discharge queue');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = async (_event: unknown, newPage: number) => {
        setPage(newPage);
        await fetchPage(newPage);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPage(value);
        setPage(0);
        setPageCursors([]);
        await fetchTotal();
        await fetchPage(0);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(0);
        setPageCursors([]);
    };

    const handleResend = async (id: string) => {
        setResendingIds((prev) => new Set(prev).add(id));
        try {
            // TODO: Wire to backend to retry auto-discharge action for the item
            await new Promise((resolve) => setTimeout(resolve, 1000));
        } finally {
            setResendingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const getStatusColor = (status: AutoDischargeItem['status']) => {
        switch (status) {
            case 'SENT':
                return 'success';
            case 'FAILED':
                return 'error';
            case 'PENDING':
                return 'warning';
            case 'COMPLETED':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatTimestamp = (ts?: Timestamp) => {
        if (!ts) return '-';
        try {
            return ts.toDate().toLocaleString();
        } catch {
            return '-';
        }
    };

    useEffect(() => {
        (async () => {
            await fetchTotal();
            await fetchPage(0);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTokens, rowsPerPage]);

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Autodischarge Queue
            </Typography>
            <Box mb={3}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by any text (email, name, user id, type)"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            </Box>
            {error && (
                <Box mb={2}>
                    <Typography variant="body1" color="error">
                        {error}
                    </Typography>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Discharge Date</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Discharge Reason</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Customer Name</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Type</strong>
                            </TableCell>
                            <TableCell>
                                <strong>User ID</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Treatment Notes</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Status</strong>
                            </TableCell>
                            <TableCell align="center">
                                <strong>Actions</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        gap={1}
                                        py={2}
                                    >
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">Loading...</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No items found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{formatTimestamp(row.discharge_date)}</TableCell>
                                    <TableCell>{row.discharge_reason ?? '-'}</TableCell>
                                    <TableCell>{row.customer_name ?? '-'}</TableCell>
                                    <TableCell>{row.type ?? '-'}</TableCell>
                                    <TableCell>{row.user_id}</TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {row.treatment_notes ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            color={getStatusColor(row.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.status === 'FAILED' && (
                                            <Tooltip title="Retry auto-discharge">
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleResend(row.id)}
                                                    disabled={resendingIds.has(row.id)}
                                                >
                                                    {resendingIds.has(row.id) ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <RefreshIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
};

export default PhioengageAutodischargeQueue;
